import { createContext, useContext, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DEFAULT_LOCALE } from './constants';
import { messages } from './messages';
import type { I18nContextValue, Locale } from './types';
import { getLocaleFromPathname, getLocalizedPath } from './routing';

const I18nContext = createContext<I18nContextValue | null>(null);

function resolveMessage(locale: Locale, key: string): string {
  const localeMessage = messages[locale][key];
  const fallbackMessage = messages[DEFAULT_LOCALE][key];

  return localeMessage ?? fallbackMessage ?? `[missing translation: ${key}]`;
}

interface I18nProviderProps {
  children: ReactNode;
}

export function I18nProvider({ children }: I18nProviderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const locale = getLocaleFromPathname(location.pathname) ?? DEFAULT_LOCALE;

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value: I18nContextValue = {
    locale,
    setLocale: (nextLocale) => {
      if (nextLocale === locale) {
        return;
      }

      navigate(getLocalizedPath(`${location.pathname}${location.search}${location.hash}`, nextLocale));
    },
    t: (key) => resolveMessage(locale, key),
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);

  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }

  return context;
}

export function useOptionalI18n(): I18nContextValue {
  const context = useContext(I18nContext);

  if (context) {
    return context;
  }

  return {
    locale: DEFAULT_LOCALE,
    setLocale: () => {},
    t: (key) => resolveMessage(DEFAULT_LOCALE, key),
  };
}
